import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage } from '../../../../../util/reactIntl';
import { convertPrice, formatMoney } from '../../../../../util/currency';
import css from './RentalRatesSheet.module.css';

const TIER_ORDER = [
  'perDayPrice',
  'overnightPrice',
  'twoDaysPrice',
  'threeDaysPrice',
  'fourDaysPrice',
  'fiveDaysPrice',
  'oneWeekPrice',
  'twentyEightDaysPrice',
];

const TIER_LABEL_IDS = {
  perDayPrice: 'RentalRatesSheet.perDay',
  overnightPrice: 'RentalRatesSheet.overnight',
  twoDaysPrice: 'RentalRatesSheet.twoDays',
  threeDaysPrice: 'RentalRatesSheet.threeDays',
  fourDaysPrice: 'RentalRatesSheet.fourDays',
  fiveDaysPrice: 'RentalRatesSheet.fiveDays',
  oneWeekPrice: 'RentalRatesSheet.oneWeek',
  twentyEightDaysPrice: 'RentalRatesSheet.twentyEightDays',
};

const SWIPE_CLOSE_THRESHOLD = 80;
const DRAG_ACTIVATION_THRESHOLD = 8;
const HORIZONTAL_DOMINANCE_RATIO = 1.2;

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 5L5 15M5 5L15 15" stroke="#667085" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const formatShortDate = (dateStr, dayOffset = 0) => {
  if (!dateStr) return '';
  // dateStr is 'YYYY-MM-DD'; parse as local date to avoid timezone shift
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d + dayOffset);
  return date.toLocaleDateString('en', { month: 'short', day: 'numeric' });
};

const RentalRatesSheet = props => {
  const {
    isOpen, onClose, title, prices, exchangeRates, selectedCurrency, intl,
    onViewListing, onSelectDates, startDate, endDate, dateRangePrice, numDays,
  } = props;
  const hasDates = !!(startDate && endDate);

  const sheetRef = useRef(null);
  const bodyRef = useRef(null);

  // ── Scroll lock ──────────────────────────────────────────────────────────────
  // Simple overflow:hidden — does NOT move the page, so there is no scroll jump
  // when the sheet closes. The position:fixed+top approach causes a visible
  // "snap to top then scroll back" animation which is what was broken.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // ── Escape key ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // ── Swipe-to-dismiss ─────────────────────────────────────────────────────────
  // Native DOM listeners (not React synthetic events) are required here because
  // the sheet is a portal attached to document.body. React 18 delegates events
  // to the #root container, which is a sibling of document.body — so after
  // setPointerCapture() the captured pointer events bubble through body→document
  // but never reach #root. React's onPointerMove/Up never fire. Attaching
  // listeners directly on the element avoids the delegation entirely.
  useEffect(() => {
    if (!isOpen) return;
    const sheet = sheetRef.current;
    if (!sheet) return;
    // On desktop the sheet is a centered modal — no swipe needed; backdrop click closes it.
    if (typeof window !== 'undefined' && window.innerWidth >= 768) return;

    let startY = 0;
    let startX = 0;
    let isDragging = false;
    let capturedPointerId = null;

    const onPointerDown = e => {
      startY = e.clientY;
      startX = e.clientX;
      isDragging = false;
      capturedPointerId = null;
      sheet.style.transition = 'none';
    };

    const onPointerMove = e => {
      const deltaY = e.clientY - startY;
      const deltaX = Math.abs(e.clientX - startX);
      const absDeltaY = Math.abs(deltaY);

      if (!isDragging) {
        // Wait for minimum movement before deciding
        if (absDeltaY < DRAG_ACTIVATION_THRESHOLD && deltaX < DRAG_ACTIVATION_THRESHOLD) return;
        // Horizontal swipe — ignore
        if (deltaX >= DRAG_ACTIVATION_THRESHOLD && deltaX > absDeltaY * HORIZONTAL_DOMINANCE_RATIO) return;
        // Upward swipe — let body scroll handle it
        if (deltaY < 0) return;
        // Downward swipe from inside the scrollable body when it isn't at top — let it scroll
        const body = bodyRef.current;
        if (body && body.contains(e.target) && body.scrollTop > 0) return;

        // Confirmed downward dismiss gesture
        isDragging = true;
        capturedPointerId = e.pointerId;
        sheet.setPointerCapture(e.pointerId);
        return;
      }

      const translateY = Math.max(0, deltaY);
      sheet.style.transform = `translateY(${translateY}px)`;
    };

    const onPointerUp = e => {
      if (!isDragging) return;
      isDragging = false;

      if (capturedPointerId !== null && sheet.hasPointerCapture(capturedPointerId)) {
        sheet.releasePointerCapture(capturedPointerId);
      }
      capturedPointerId = null;

      const deltaY = e.clientY - startY;
      if (deltaY > SWIPE_CLOSE_THRESHOLD) {
        sheet.style.transition = 'transform 0.2s ease-out';
        sheet.style.transform = 'translateY(100%)';
        setTimeout(onClose, 200);
      } else {
        sheet.style.transition = 'transform 0.25s ease-out';
        sheet.style.transform = '';
      }
    };

    const onPointerCancel = () => {
      if (!isDragging) return;
      isDragging = false;
      capturedPointerId = null;
      sheet.style.transition = 'transform 0.25s ease-out';
      sheet.style.transform = '';
    };

    sheet.addEventListener('pointerdown', onPointerDown);
    sheet.addEventListener('pointermove', onPointerMove);
    sheet.addEventListener('pointerup', onPointerUp);
    sheet.addEventListener('pointercancel', onPointerCancel);

    return () => {
      sheet.removeEventListener('pointerdown', onPointerDown);
      sheet.removeEventListener('pointermove', onPointerMove);
      sheet.removeEventListener('pointerup', onPointerUp);
      sheet.removeEventListener('pointercancel', onPointerCancel);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const tiers = TIER_ORDER
    .filter(key => prices?.[key]?.amount && prices?.[key]?.currency)
    .map(key => {
      const raw = prices[key];
      const converted = convertPrice(exchangeRates, raw.amount, raw.currency, selectedCurrency);
      if (!converted) return null;
      return {
        key,
        labelId: TIER_LABEL_IDS[key],
        formatted: formatMoney(intl, converted),
      };
    })
    .filter(Boolean);

  const sheet = (
    <div
      className={css.overlay}
      onClick={e => { e.stopPropagation(); onClose(); }}
    >
      <div
        ref={sheetRef}
        className={css.sheet}
        onClick={e => e.stopPropagation()}
      >
        <div className={css.dragHandleArea}>
          <div className={css.dragHandle} />
        </div>

        <div className={css.header}>
          <span className={css.listingTitle}>{title}</span>
          <button
            className={css.closeBtn}
            onClick={e => { e.stopPropagation(); onClose(); }}
            aria-label={intl.formatMessage({ id: 'RentalRatesSheet.close' })}
          >
            <CloseIcon />
          </button>
        </div>

        <div ref={bodyRef} className={css.body}>
          {hasDates && (
            <div className={css.selectedDateRange}>
              <span className={css.dateRangeLabel}>
                {formatShortDate(startDate)} – {formatShortDate(endDate, -1)}
                {numDays > 0 && <span className={css.dateRangeDays}> · {numDays} {numDays === 1 ? 'day' : 'days'}</span>}
              </span>
              {dateRangePrice && (
                <span className={css.dateRangeTotalPrice}>
                  {formatMoney(intl, dateRangePrice)} total
                </span>
              )}
            </div>
          )}

          <p className={css.tiersHeading}>
            <FormattedMessage id="RentalRatesSheet.tiersHeading" />
          </p>

          {tiers.length > 0 && (
            <div className={css.tiersGrid}>
              {tiers.map(({ key, labelId, formatted }) => (
                <div key={key} className={css.tierCell}>
                  <span className={css.tierLabel}>
                    <FormattedMessage id={labelId} />
                  </span>
                  <span className={css.tierPrice}>{formatted}</span>
                </div>
              ))}
              {tiers.length % 2 !== 0 && <div className={css.tierCellFiller} />}
            </div>
          )}

          <p className={css.tagline}>
            <FormattedMessage id="RentalRatesSheet.tagline" />
          </p>
        </div>

        <div className={css.actions}>
          <button className={css.viewListingBtn} onClick={e => { e.stopPropagation(); onViewListing(e); }}>
            <FormattedMessage id="RentalRatesSheet.viewListing" />
          </button>
          <button className={css.selectDatesBtn} onClick={e => { e.stopPropagation(); onSelectDates(e); }}>
            {hasDates
              ? <FormattedMessage id="RentalRatesSheet.changeDates" />
              : <FormattedMessage id="RentalRatesSheet.selectDates" />
            }
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? ReactDOM.createPortal(sheet, document.body)
    : null;
};

export default RentalRatesSheet;
